import React from 'react';
import { Autocomplete, TextField, Chip, createFilterOptions, Box, Typography } from '@mui/material';
import { DEFAULT_TAG_COLOR, resolveTagColor, TAG_COLORS } from '../../../../utils/tagColors';

const normalizeTag = (tag) => {
  if (!tag) return null;
  if (typeof tag === 'string') {
    const name = tag.trim();
    return name ? { name, color: DEFAULT_TAG_COLOR } : null;
  }
  if (tag.inputValue) {
    const name = String(tag.inputValue).trim();
    return name ? { name, color: tag.color || DEFAULT_TAG_COLOR } : null;
  }
  if (typeof tag === 'object') {
    const name = (tag.name || tag.label || '').trim();
    if (!name) return null;
    return {
      name,
      color: (tag.color || DEFAULT_TAG_COLOR).trim() || DEFAULT_TAG_COLOR,
    };
  }
  return null;
};

const filter = createFilterOptions({
  stringify: (option) => (typeof option === 'string' ? option : option?.name || ''),
});

const TagSelect = ({
  value,
  onChange,
  availableTags = [],
  label,
  placeholder,
}) => {
  const [selectedColor, setSelectedColor] = React.useState(DEFAULT_TAG_COLOR);

  const normalizedValue = React.useMemo(() => {
    const unique = new Map();
    (Array.isArray(value) ? value : []).forEach((item) => {
      const normalized = normalizeTag(item);
      if (!normalized) return;
      const key = normalized.name.toLowerCase();
      if (!unique.has(key)) {
        unique.set(key, normalized);
      }
    });
    return Array.from(unique.values());
  }, [value]);

  const handleChange = (_, newValue) => {
    const existingNames = new Set(
      availableTags.map((tag) => (tag.name || '').toLowerCase())
    );
    const dedup = new Map();

    newValue.forEach((item) => {
      const normalized = normalizeTag(item);
      if (!normalized) return;
      const key = normalized.name.toLowerCase();
      if (!dedup.has(key)) {
        const inputWasNew = !existingNames.has(key);
        const color = inputWasNew && (!normalized.color || normalized.color === DEFAULT_TAG_COLOR)
          ? selectedColor
          : normalized.color;

        dedup.set(key, { ...normalized, color });
      }
    });

    onChange?.(Array.from(dedup.values()));
  };

  return (
    <Box>
      <Autocomplete
        multiple
        freeSolo
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        value={normalizedValue}
        options={availableTags}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name || '')}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          const { inputValue } = params;
          const trimmed = inputValue.trim();
          const hasMatch = options.some(
            (option) => option?.name?.toLowerCase() === trimmed.toLowerCase()
          );

          if (trimmed !== '' && !hasMatch) {
            filtered.push({
              name: trimmed,
              inputValue: trimmed,
              color: selectedColor,
              isNew: true,
            });
          }

          return filtered;
        }}
        isOptionEqualToValue={(option, val) => {
          const optName = (option?.name || option?.inputValue || '').toLowerCase();
          const valName = (val?.name || val?.inputValue || '').toLowerCase();
          return optName === valName;
        }}
        onChange={handleChange}
        renderOption={(props, option) => {
          const name = option?.name || option?.inputValue || '';
          const color = resolveTagColor(option?.color);
          const isNew = Boolean(option?.isNew);
          return (
            <li {...props}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: color,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: isNew ? 600 : 500 }}>
                  {isNew ? `Add "${name}"` : name}
                </Typography>
              </Box>
            </li>
          );
        }}
          renderTags={(tags, getTagProps) =>
          tags.map((tag, index) => (
            <Chip
              {...getTagProps({ index })}
              label={tag.name}
              sx={{ backgroundColor: resolveTagColor(tag.color), color: '#fff' }}
          />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            label={label}
            placeholder={placeholder}
          />
        )}
      />

      <Box sx={{ mt: 1.5 }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.75 }}>
          Color
        </Typography>
        <Box component="ul" sx={{ display: 'flex', gap: 0.75, p: 0, m: 0, listStyle: 'none' }}>
          {TAG_COLORS.map((color) => {
            const resolved = resolveTagColor(color);
            const isActive = selectedColor === color;
            return (
              <Box
                key={color}
                component="button"
                type="button"
                onClick={() => setSelectedColor(color)}
                aria-label={color}
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  border: '1px solid',
                  borderColor: isActive ? '#000' : 'rgba(0, 0, 0, 0.16)',
                  backgroundColor: resolved,
                  cursor: 'pointer',
                  transition: 'opacity 120ms ease, box-shadow 120ms ease',
                  '&:hover': {
                    opacity: 0.85,
                    boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.06) inset',
                  },
                  '&:focus-visible': {
                    outline: '2px solid rgba(0, 0, 0, 0.6)',
                    outlineOffset: '1px',
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default TagSelect;
